import Axios from "axios";
import Bugsnag from "@bugsnag/js";
import { Response } from "@konv/http";

export const SaiposEnvironment = {
  SANDBOX: "SANDBOX",
  PRODUCTION: "PRODUCTION",
};

const SaiposBaseURL = {
  [SaiposEnvironment.SANDBOX]: "https://homolog-order-api.saipos.com",
  [SaiposEnvironment.PRODUCTION]: "https://order-api.saipos.com",
};

export class SaiposResponse extends Response {
  constructor(data = null, code, errors = []) {
    super(data, code, errors, null);
  }

  static fromResponse(response) {
    const isResponse = !!response?.data?.response;
    if (isResponse) {
      const code = response.data.response.code;
      const data = response.data.response.data;
      const errors = response.data.response.errors || [];
      return new SaiposResponse(data, code, errors);
    }

    const status = response.status || 500;
    const payload = response.data || {};

    if (status >= 400) {
      const errorMessage =
        payload?.errorMessage ||
        `Falha na comunicação com a Saipos (${status})`;
      return new SaiposResponse(null, 1000, [errorMessage]);
    }

    const result = payload || null;

    return new SaiposResponse(result, 200, []);
  }

  static fromError(error) {
    const response = error?.response;

    if (response) {
      return SaiposResponse.fromResponse(response);
    }

    return new SaiposResponse(null, 500, [error.message]);
  }

  isError() {
    return this.getErrors([]).length > 0;
  }

  isSuccess() {
    return this.getErrors([]).length === 0;
  }
}

export class SaiposAPI {
  constructor({
    storeId,
    partnerId,
    secret,
    environment,
    authService,
    baseURL,
  }) {
    this.storeId = storeId;
    this.partnerId = partnerId;
    this.secret = secret || process.env.SAIPOS_SECRET;
    this.environment = environment || SaiposEnvironment.PRODUCTION;

    this.authService = authService;

    this.client = Axios.create({
      baseURL: baseURL ?? SaiposBaseURL[this.environment],
      headers: {
        "x-cod-store": storeId,
        "x-id-partner": partnerId,
      },
    });
  }

  async request({
    endpoint,
    method,
    params = {},
    data,
    headers = {},
    allowsRetry = true,
  }) {
    let token = null;

    if (this.authService) {
      token = await this.authService.getToken();
    }

    headers = {
      ...headers,
      ...(token && { Authorization: token }),
      "Content-Type": "application/json",
    };

    try {
      const response = await this.client.request({
        url: endpoint,
        method,
        params,
        data,
        headers,
      });

      console.log(
        "[HTTP][SAIPOS][%s] %s %s %s %s %s",
        this.storeId,
        method,
        endpoint,
        response.status,
        JSON.stringify(data),
        JSON.stringify(response?.data),
      );

      Bugsnag.leaveBreadcrumb(
        "Saipos - Requisição",
        {
          url: endpoint,
          method,
          data,
          query: params,
          status: response?.status,
          response: response?.data,
        },
        "request"
      );

      return SaiposResponse.fromResponse(response);
    } catch (err) {
      console.log(
        "[HTTP][SAIPOS][%s] %s %s %s %s %s",
        this.storeId,
        method,
        endpoint,
        err?.response?.status || 500,
        JSON.stringify(data),
        JSON.stringify(err?.response?.data),
      );

      Bugsnag.leaveBreadcrumb(
        "Saipos - Requisição",
        {
          url: endpoint,
          method,
          data,
          query: params,
          status: err?.response?.status,
          response: err?.response?.data,
        },
        "request"
      );

      if (allowsRetry && this.authService && err?.response?.status === 401) {
        await this.authService.removeToken();

        return await this.request({
          endpoint,
          method,
          params,
          data,
          headers,
          allowsRetry: false,
        });
      }

      return SaiposResponse.fromError(err);
    }
  }

  async getToken() {
    const response = await this.request({
      endpoint: "/auth",
      method: "POST",
      data: {
        idPartner: this.partnerId,
        secret: this.secret,
      },
    });

    return response;
  }

  /**
   * Criar um novo pedido
   * @param {} order Dados do pedido
   */
  async createOrder(order) {
    const response = await this.request({
      endpoint: "/order",
      method: "POST",
      data: order,
    });

    return response;
  }

  async getTableById(tableId) {
    const response = await this.request({
      endpoint: `/sale-status-by-table-or-pad`,
      method: "GET",
      params: {
        table: !isNaN(tableId) ? `[${tableId}]` : tableId,
      },
    });

    return response;
  }

  async convertPosTable (tableId) {
    const response = await this.request({
      endpoint: `/convert-orders-in-table`,
      method: "POST",
      data: {
        table: tableId,
      },
    });

    return response;
  }

  async getOrderById(orderId) {
    const response = await this.request({
      endpoint: `/order`,
      method: "GET",
      params: {
        cod_store: this.storeId,
        order_id: orderId,
      },
    });

    return response;
  }

  async getOrderByIds(orderIds) {
    const result = await Promise.all(
      orderIds.map((orderId) => this.getOrderById(orderId)),
    );

    return result;
  }

  async closeTableByOrderId(orderId) {
    const response = await this.request({
      endpoint: `/close-sale`,
      method: "POST",
      data: {
        cod_store: this.storeId,
        order_id: orderId,
      },
    });

    return response;
  }
}
